/**
 * The covenant ledger as the gallery reads it: what each resale settlement
 * owes, the arithmetic that produced it, and which one control the row gets.
 *
 * Pure. Settlements, the gallery's own placements and thumbnails go in; the
 * four groups, the figures panel and the totals line come out. The copy is
 * `settlements.constants.ts`; the two reads, the settle and unlock writes and
 * the rate dialog are `useSettlementsController.ts`. The artist's reading of
 * the same rows is `collector/artistSettlements.model.ts`, which discloses
 * less and is a separate file for that reason.
 *
 * The locked group comes from placements, not settlements — a covenant still
 * running has produced nothing to list, and it is exactly the row a director
 * looks here for. An unlocked covenant is not an expired one, a share nobody
 * has computed prints a dash and never £0, and the totals line sums only what
 * is both open and priced, in one currency, because the figure a director
 * acts on is on the row.
 */

import type { PlacedWork, Settlement, SettlementState } from "@ondo/shared";
import {
  artistWasNotElected,
  covenantState,
  needsRate,
  percentOfBp,
  settlementGroup,
  settlementState,
} from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { money as sharedMoney } from "../lib/money.ts";
import { GALLERY_MEMBERSHIP_LOST } from "./gallery.constants.ts";
import { SETTLEMENT_COPY } from "./settlements.constants.ts";
import { formatConsignedDate } from "./gallery.model.ts";
import { formatRemaining } from "./soldWorks.model.ts";

/** One label/value pair of the arithmetic panel. */
export interface Figure {
  label: string;
  value: string;
  /** A loss, which is the one figure on the panel that is not a plain number. */
  negative?: boolean;
  /** True when the row is waiting on a rate and this figure does not exist. */
  pending?: boolean;
}

/**
 * The one control a row may carry, with its label decided here.
 *
 * A discriminated shape rather than a string, so the view switches on `kind`
 * and reads the leg off the row — it never has to know that "collect" and
 * "remit" are the two legs of one act and "rate" is something else.
 */
export type SettlementAction =
  | { kind: "rate"; label: string }
  | { kind: "settle"; leg: "collect" | "remit"; label: string };

/** One settlement, with every label already decided. */
export interface SettlementRow {
  settlementId: string;
  workId: string;
  ondoCode: string;
  title: string;
  /**
   * 'John Currin · resold 10 Jul 2026 by James Chen → Private collection, Geneva'.
   *
   * ONE line, because the design draws one: who made it and what happened to it
   * are read together, and splitting them put a two-word orphan above a
   * sentence that already carried the artist's name in the work's own record.
   */
  line: string;
  /** 'OND-2000-JC-4408 · Recorded by Thaddaeus Ropac (ONDO-1030)'. */
  codeLine: string;
  state: SettlementState;
  /** 'Settlement due', 'Settled', 'Awaiting a rate'… */
  stateLabel: string;
  /** 'Post-sale covenant · fixed 12 Mar 2023 · term to 12 Mar 2033'. */
  termsLabel: string;
  /** The six-cell arithmetic panel, in reading order. */
  figures: Figure[];
  /** '£172,000', or "—" when nothing has been computed. */
  dueToGallery: string;
  /** The sentence under the panel. Never empty. */
  note: string;
  /** The control, or null when there is nothing this gallery can do. */
  action: SettlementAction | null;
  /**
   * 'GBP per 1 USD' — the rate field's label, decided here.
   *
   * On the row rather than worked out in the view, because which currency goes
   * on which side of that sentence is a domain rule: the rate converts the
   * RESALE into the COVENANT's currency, and getting it backwards would invite
   * a director to type the reciprocal of the number they meant.
   */
  rateLabel: string;
  href: string;
  imageUrl: string | null;
}

/**
 * A live covenant that stops the work being sold on.
 *
 * NOT a settlement, and it is on this screen anyway. A locking covenant can
 * never produce a settlement until somebody lifts the lock, so the screen that
 * lists settlements is where a director will look for the reason one is
 * missing — and `unlockCovenant()` has had no caller anywhere in the app since
 * it was written.
 */
export interface LockedRow {
  transferId: string;
  workId: string;
  title: string;
  identity: string;
  /** 'sold 14 Mar 2024 to Isabelle Marchetti · 7y 6m remaining'. */
  sale: string;
  /** '35% of profit', or "" when the covenant shares nothing. */
  wouldOwe: string;
  href: string;
  imageUrl: string | null;
}

export type SettlementsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | {
      kind: "ready";
      due: SettlementRow[];
      closed: SettlementRow[];
      nothingOwed: SettlementRow[];
      locked: LockedRow[];
      /** 'covenant ledger · 3 settlements'. */
      summary: string;
      /** '£172,000 to this gallery · £172,000 to pass on'. */
      dueSummary: string;
    };

/**
 * Everything the screen renders, decided here.
 *
 * `now` is a parameter rather than a call inside, the rule `toSoldWorksView`
 * keeps — every date-dependent label here resolves against one clock, so a test
 * can put a covenant's term on either side of an expiry without waiting a
 * decade.
 *
 * TWO SOURCES, and they answer different questions. `settlements` is what the
 * covenants have already produced; `placements` is the gallery's own ledger,
 * read for the ONE thing settlements cannot show — a covenant still running.
 * A screen that derived the locked group from settlements would list only locks
 * that had already been lifted.
 */
export function toSettlementsView(input: {
  settlements: Settlement[] | null;
  placements: PlacedWork[] | null;
  error: string | null;
  images: ReadonlyMap<string, string>;
  workBase: string;
  now?: Date;
}): SettlementsView {
  const { settlements, placements, error, images, workBase } = input;
  const now = input.now ?? new Date();

  if (error) return { kind: "error", title: SETTLEMENT_COPY.errorTitle, message: error };
  // The settlements are the subject; the placements are supporting evidence. A
  // screen that waited for both would be blank while the second request ran,
  // and the locked group can appear a beat later without misleading anybody.
  if (!settlements) return { kind: "loading", message: SETTLEMENT_COPY.loading };

  const locked = (placements ?? [])
    .filter((each) => isLocked(each, now))
    .map((each) => toLockedRow(each, workBase, images, now));

  if (settlements.length === 0 && locked.length === 0) {
    return {
      kind: "empty",
      message: SETTLEMENT_COPY.empty,
      detail: SETTLEMENT_COPY.emptyDetail,
    };
  }

  const rows = settlements.map((each) => toRow(each, workBase, images));
  const due = rows.filter((row) => settlementGroup(row.state) === "due");

  return {
    kind: "ready",
    due,
    closed: rows.filter((row) => settlementGroup(row.state) === "closed"),
    nothingOwed: rows.filter((row) => settlementGroup(row.state) === "nothing_owed"),
    locked,
    summary: SETTLEMENT_COPY.ledger(rows.length),
    dueSummary: describeDue(settlements),
  };
}

/**
 * Whether this sale's covenant still stops the work being sold on.
 *
 * All four conditions, and the third is the one worth naming: an UNLOCKED
 * covenant is not an expired one. `ondo.unlock_covenant()` records a release
 * and never edits a term, so the row still carries its share — what it no
 * longer does is stop the work moving, which is the only thing this group is
 * about.
 */
export function isLocked(placement: PlacedWork, now: Date): boolean {
  const covenant = placement.covenant;
  if (!covenant) return false;

  return (
    covenant.locksTransfer &&
    covenant.unlockedAt === null &&
    covenantState(covenant, now) === "active"
  );
}

function toLockedRow(
  placement: PlacedWork,
  workBase: string,
  images: ReadonlyMap<string, string>,
  now: Date,
): LockedRow {
  const covenant = placement.covenant;
  const sold = `${SETTLEMENT_COPY.soldOn} ${formatConsignedDate(placement.soldAt)}`;
  const to = placement.buyerName ? ` ${SETTLEMENT_COPY.toBuyer} ${placement.buyerName}` : "";
  const remaining = covenant ? formatRemaining(covenant.endsAt, now) : "";

  return {
    transferId: placement.transferId,
    workId: placement.workId,
    title: placement.title,
    identity: [placement.attributedName, placement.year].filter(Boolean).join(" · "),
    sale: [`${sold}${to}`, remaining].filter(Boolean).join(" · "),
    // A covenant that locks but shares nothing is a pure standstill, and saying
    // "0% of profit" would invite a director to read it as a broken term.
    wouldOwe:
      covenant && covenant.resaleShareBp > 0
        ? SETTLEMENT_COPY.wouldOwe(percentOfBp(covenant.resaleShareBp))
        : "",
    href: `${workBase}/${placement.workId}`,
    imageUrl: images.get(placement.workId) ?? null,
  };
}

function toRow(
  settlement: Settlement,
  workBase: string,
  images: ReadonlyMap<string, string>,
): SettlementRow {
  const state = settlementState(settlement);

  return {
    settlementId: settlement.settlementId,
    workId: settlement.workId,
    ondoCode: settlement.ondoCode,
    title: settlement.title,
    line: [settlement.attributedName, describeResale(settlement)]
      .filter(Boolean)
      .join(" · "),
    codeLine: [settlement.ondoCode, describeRecordedBy(settlement)]
      .filter(Boolean)
      .join(" · "),
    state,
    stateLabel: SETTLEMENT_COPY.state[state],
    termsLabel: describeTerms(settlement),
    figures: describeFigures(settlement),
    dueToGallery: money(settlement.galleryMinor, settlement.currency),
    note: describeNote(settlement, state),
    action: actionFor(settlement, state),
    rateLabel: SETTLEMENT_COPY.rate.label(
      settlement.resaleCurrency,
      settlement.currency,
    ),
    href: `${workBase}/${settlement.workId}`,
    imageUrl: images.get(settlement.workId) ?? null,
  };
}

/**
 * '£172,000', or an em dash when the figure does not exist yet.
 *
 * A dash rather than '£0': a share nobody has computed is not a share that came
 * to nothing, and printing zero would announce that a resale made no profit
 * before anybody has said what it made.
 *
 * The rule itself lives in `lib/money.ts` — the artist's settlements screen had
 * a byte-identical copy of it. This binds this screen's own placeholder copy so
 * the call sites below stay as they read.
 */
export function money(minor: number | null, currency: string): string {
  return sharedMoney(minor, currency, SETTLEMENT_COPY.pending);
}

/** 'resold 10 Jul 2026 by James Chen → Private collection, Geneva'. */
export function describeResale(settlement: Settlement): string {
  const when = `${SETTLEMENT_COPY.resoldOn} ${formatConsignedDate(settlement.resaleSoldAt)}`;
  const by = settlement.resellerName
    ? ` ${SETTLEMENT_COPY.byReseller} ${settlement.resellerName}`
    : "";
  const to = settlement.resaleBuyerName ? ` → ${settlement.resaleBuyerName}` : "";

  return `${when}${by}${to}`;
}

/**
 * 'Recorded by Blum & Poe (ONDO-2277)'.
 *
 * Named because it is usually NOT the gallery reading the screen — a resale
 * runs through whichever gallery the new owner consigned to — and a director's
 * first question about money they did not know was owed is who sold it.
 */
export function describeRecordedBy(settlement: Settlement): string {
  if (!settlement.resaleGalleryName) return "";
  const id = settlement.resaleGalleryOndoId ? ` (${settlement.resaleGalleryOndoId})` : "";

  return `${SETTLEMENT_COPY.recordedBy} ${settlement.resaleGalleryName}${id}`;
}

/** 'Post-sale covenant · fixed 12 Mar 2023 · term to 12 Mar 2033'. */
export function describeTerms(settlement: Settlement): string {
  return [
    SETTLEMENT_COPY.covenantTerms,
    `${SETTLEMENT_COPY.fixedOn} ${formatConsignedDate(settlement.originalSoldAt)}`,
    `${SETTLEMENT_COPY.termTo} ${formatConsignedDate(settlement.covenantEndsAt)}`,
  ].join(" · ");
}

/**
 * The six cells of the arithmetic, in the order a reader checks them.
 *
 * The two prices, then what they made, then the share, then the two legs — so
 * the panel reads as the sum it is rather than as a table of facts. Every value
 * comes off the row; nothing here multiplies anything.
 *
 * THE RESALE PRICE IS SHOWN IN ITS OWN CURRENCY, always, even once a rate has
 * converted it. The converted figure is what the share was taken from, and both
 * are shown, because a settlement disputed later is disputed about exactly that
 * step.
 */
export function describeFigures(settlement: Settlement): Figure[] {
  const { currency, resaleCurrency } = settlement;
  const converted = resaleCurrency !== currency;
  const pending = settlement.shareMinor === null;

  const figures: Figure[] = [
    {
      label: SETTLEMENT_COPY.figures.original,
      value: money(settlement.originalMinor, currency),
    },
    {
      label: SETTLEMENT_COPY.figures.resale,
      value: money(settlement.resaleMinor, resaleCurrency),
    },
  ];

  if (converted) {
    figures.push({
      label: SETTLEMENT_COPY.figures.converted(resaleCurrency, currency),
      value: money(settlement.resaleInCurrencyMinor, currency),
      pending,
    });
  }

  figures.push(
    {
      label: SETTLEMENT_COPY.figures.profit,
      value: money(settlement.profitMinor, currency),
      negative: settlement.profitMinor !== null && settlement.profitMinor < 0,
      pending,
    },
    {
      label: SETTLEMENT_COPY.figures.share(percentOfBp(settlement.shareBp)),
      value: money(settlement.shareMinor, currency),
      pending,
    },
    {
      label: SETTLEMENT_COPY.figures.gallery(percentOfBp(settlement.gallerySplitBp)),
      value: money(settlement.galleryMinor, currency),
      pending,
    },
    {
      label: SETTLEMENT_COPY.figures.artist(percentOfBp(settlement.artistSplitBp)),
      // A covenant the artist did not elect owes them nothing, and the row still
      // carries their split. Printing '£0' beside '40%' with no explanation
      // reads as a bug in the arithmetic; the note under the panel is where the
      // explanation goes.
      value: money(settlement.artistMinor, currency),
      pending,
    },
  );

  return figures;
}

/**
 * The sentence under the panel: what happened, or what has to happen next.
 *
 * Never empty. Every branch of the state is answered here, because this is the
 * one place a row explains itself — and the states a director will not expect
 * (void, awaiting a rate, an artist who was never elected) are exactly the ones
 * that need it most.
 */
export function describeNote(settlement: Settlement, state: SettlementState): string {
  if (state === "void") return SETTLEMENT_COPY.notes.void;
  if (state === "awaiting_rate") {
    return SETTLEMENT_COPY.notes.awaitingRate(
      settlement.resaleCurrency,
      settlement.currency,
    );
  }
  if (state === "nothing_owed") {
    return artistWasNotElected(settlement)
      ? SETTLEMENT_COPY.notes.notElected
      : SETTLEMENT_COPY.notes.noProfit;
  }

  if (state === "settled") {
    const collected = `${SETTLEMENT_COPY.notes.collected} ${formatConsignedDate(
      settlement.collectedAt ?? "",
    )}`;
    const to = settlement.artistName ? ` ${settlement.artistName}` : "";
    const remitted = `${SETTLEMENT_COPY.notes.remitted}${to} ${formatConsignedDate(
      settlement.remittedAt ?? "",
    )}`;
    const answer = describeArtistAnswer(settlement);

    return [collected, remitted, answer].filter(Boolean).join(" · ");
  }

  if (state === "collected") {
    // The artist's half of a covenant they did not elect is zero, so there is
    // nothing to pass on and the row is finished in every sense that matters.
    if (settlement.artistMinor === 0) return SETTLEMENT_COPY.notes.collectedNoArtist;
    return settlement.artistOnOndo
      ? SETTLEMENT_COPY.notes.collected1
      : SETTLEMENT_COPY.notes.collectedOffRegistry;
  }

  return SETTLEMENT_COPY.notes.due;
}

/** '✓ confirmed by the artist', or the dispute, or "" when unanswered. */
export function describeArtistAnswer(settlement: Settlement): string {
  if (settlement.artistAnsweredAt === null) return "";
  if (settlement.artistConfirmed === false) {
    const why = settlement.artistNote ? `: ${settlement.artistNote}` : "";
    return `${SETTLEMENT_COPY.notes.disputed}${why}`;
  }

  return SETTLEMENT_COPY.notes.confirmed;
}

/**
 * Which control this row gets, and null for most of them.
 *
 * DERIVED FROM THE ROW, never from the group: 'nothing owed' and 'void' are
 * both unactionable and reach that for different reasons, and a screen that
 * hid the button by group would also hide it from a row that had simply not
 * been computed yet.
 */
export function actionFor(
  settlement: Settlement,
  state: SettlementState,
): SettlementRow["action"] {
  if (needsRate(settlement)) return { kind: "rate", label: SETTLEMENT_COPY.actions.rate };
  if (state === "due") return { kind: "settle", leg: "collect", label: SETTLEMENT_COPY.actions.collect };
  // Nothing to pass on when the artist's half is zero, so a remit control here
  // would be a button that records a payment of nothing.
  if (state === "collected" && (settlement.artistMinor ?? 0) > 0) {
    return { kind: "settle", leg: "remit", label: SETTLEMENT_COPY.actions.remit };
  }

  return null;
}

/**
 * '£172,000 to this gallery · £172,000 to pass on'.
 *
 * Sums only what is BOTH open and computed. A row awaiting a rate contributes
 * nothing to either figure and is counted separately, because adding a null as
 * zero would understate the total and saying so is the whole point of the line.
 */
export function describeDue(settlements: Settlement[]): string {
  const open = settlements.filter(
    (each) => each.voidedAt === null && each.collectedAt === null,
  );
  const waiting = open.filter((each) => each.shareMinor === null).length;
  const priced = open.filter((each) => each.shareMinor !== null);

  if (priced.length === 0 && waiting === 0) return "";

  const currency = priced[0]?.currency ?? settlements[0]?.currency ?? "GBP";
  // ONE CURRENCY, and it is the first row's. Every settlement is denominated in
  // its own covenant's currency, so a gallery with terms in two currencies gets
  // a total in the wrong one — which is why this line is a summary and the
  // figure a director acts on is on the row.
  const mixed = priced.some((each) => each.currency !== currency);

  const gallery = priced.reduce((sum, each) => sum + (each.galleryMinor ?? 0), 0);
  const artist = priced.reduce((sum, each) => sum + (each.artistMinor ?? 0), 0);

  const parts = [];
  if (priced.length > 0 && !mixed) {
    parts.push(SETTLEMENT_COPY.dueToGallery(money(gallery, currency)));
    if (artist > 0) parts.push(SETTLEMENT_COPY.dueToArtist(money(artist, currency)));
  }
  if (waiting > 0) parts.push(SETTLEMENT_COPY.awaitingCount(waiting));

  return parts.join(" · ");
}

/**
 * What went wrong, in words a gallery member can act on.
 *
 * A 403 on this route is not a mistyped id — it takes none. It means the caller
 * holds no gallery membership at all, which is what `describeSoldWorksError`
 * says about the same failure on the same rail.
 */
export function describeSettlementsError(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 403) {
    return GALLERY_MEMBERSHIP_LOST;
  }

  return cause instanceof Error ? cause.message : SETTLEMENT_COPY.errorTitle;
}
